/**
 * PLP Category page behaviour
 * ------------------------------------------------------------------
 * - Keeps product images lazy so the PLP can become interactive quickly.
 * - Intercepts clicks on [data-plp-pill] and swaps only the
 *   plp-category section by fetching the full target page, parsing
 *   it, and replacing the current section wrapper in the DOM.
 *   The custom PLP header, footer and page chrome stay in place.
 * - If the swap fails for any reason, the link falls back to a
 *   normal page load so the category is never stuck.
 */
(function () {
  'use strict';

  var DEBUG = /[?&]plp-debug=1/.test(window.location.search);
  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[PLP]'].concat(args));
  }

  function warn() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.warn.apply(console, ['[PLP]'].concat(args));
  }

  /* ------------------------------------------------------------------
     Per-section helpers
     ------------------------------------------------------------------ */

  function initDust(section) {
    var dustContainer = section.querySelector('.plp__dust');
    if (!dustContainer) return;

    // Avoid stacking particles if this section is re-initialised.
    dustContainer.innerHTML = '';
    if (window.matchMedia('(max-width: 1023px)').matches) return;

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var particleCount = prefersReducedMotion ? 0 : 18;
    if (!particleCount) return;

    var rect = dustContainer.getBoundingClientRect();
    var sectionRect = section.getBoundingClientRect();
    var containerWidth = rect.width || sectionRect.width || section.offsetWidth || window.innerWidth || 390;
    var containerHeight = rect.height || sectionRect.height || section.offsetHeight || window.innerHeight || 844;

    var RAY_ANGLES = [21, 29, 37, 44, 53, 62, 71, 79, 89];
    var sunX = -0.02 * containerWidth;
    var sunY = 0.2 * Math.min(1.146 * containerWidth, 0.78 * (window.innerHeight || 844));
    var maxDist = Math.sqrt(containerWidth * containerWidth + containerHeight * containerHeight);

    var fragment = document.createDocumentFragment();

    for (var i = 0; i < particleCount; i++) {
      var angle = RAY_ANGLES[(Math.random() * RAY_ANGLES.length) | 0] + (Math.random() - 0.5) * 9;
      var rad = angle * Math.PI / 180;
      var dist = Math.pow(Math.random(), 1.1) * maxDist;
      var startX = sunX + Math.cos(rad) * dist;
      var startY = sunY + Math.sin(rad) * dist;

      if (startX < 0 || startX > containerWidth || startY < 0 || startY > containerHeight) {
        startX = Math.random() * containerWidth;
        startY = Math.random() * containerHeight;
      }

      var falloff = 1 - Math.min(dist / maxDist, 1) * 0.55;
      var depth = Math.pow(Math.random(), 3);
      var size = 3 + depth * 8;
      // The radial fill already contains the soft falloff. Expand its box by
      // the old blur reach instead of assigning a CSS filter to every mote;
      // this keeps the same approximate footprint without creating 18
      // independently filtered animated surfaces.
      var softness = 0.4 + depth * 3.4;
      var visualSize = size + softness * 2;
      var travel = 70 + Math.random() * 150;
      var perp = (Math.random() - 0.5) * 34;
      var dx = Math.cos(rad) * travel - Math.sin(rad) * perp;
      var dy = Math.sin(rad) * travel + Math.cos(rad) * perp;
      var duration = 16 + Math.random() * 20;
      var delay = Math.random() * -duration;
      var opacity = (0.7 + Math.random() * 0.3) * falloff * (1 - depth * 0.25);

      var p = document.createElement('span');
      p.className = 'plp__dust-particle';
      p.style.cssText =
        'width:' + visualSize + 'px;' +
        'height:' + visualSize + 'px;' +
        'left:' + (startX - softness) + 'px;' +
        'top:' + (startY - softness) + 'px;' +
        '--dust-dx:' + dx + 'px;' +
        '--dust-dy:' + dy + 'px;' +
        '--dust-opacity:' + opacity + ';' +
        'animation: plp-dust-float ' + duration + 's linear ' + delay + 's infinite;';

      fragment.appendChild(p);
    }

    dustContainer.appendChild(fragment);
  }

  function teardownHeroReveal(section) {
    if (!section) return;
    var hero = section.querySelector('[data-plp-hero-video]');
    if (hero && hero._plpHeroCleanup) {
      hero._plpHeroCleanup();
      hero._plpHeroCleanup = null;
    }
    section.classList.remove('plp--hero-pending', 'plp--hero-ready');

    var desktopHero = section.querySelector('.plp__hero');
    if (desktopHero && desktopHero._plpDesktopVideoCleanup) {
      desktopHero._plpDesktopVideoCleanup();
      desktopHero._plpDesktopVideoCleanup = null;
    }
  }

  /**
   * Cancels a hero film's download and takes it out of the document. Removing
   * it is the point: a <video> that is merely hidden still holds its decoded
   * frames, and one left with an autoplay attribute still fetches the file.
   */
  function stripHeroMedia(media) {
    if (!media) return;

    var video = media.querySelector('video');
    if (video) {
      try {
        video.pause();
      } catch (error) {
        /* pause() can reject on a not-yet-loaded element; nothing to undo. */
      }
      video.removeAttribute('autoplay');
      video.removeAttribute('src');
      while (video.firstChild) video.removeChild(video.firstChild);
      try {
        video.load();
      } catch (error) {
        /* load() only needs to cancel the pending fetch; failure is harmless. */
      }
    }

    if (media.parentNode) media.parentNode.removeChild(media);
  }

  /**
   * The mobile hero film is the portrait cut with the Smart-Animate reveal.
   * From 1024px the desktop banner takes over — its own landscape film if the
   * collection has one, otherwise the still — so the portrait film is removed
   * from the DOM rather than hidden: a display:none
   * <video autoplay preload="auto"> still downloads and decodes the whole
   * file, and the reveal controller would otherwise sit waiting on readiness
   * events for media nobody can see.
   */
  function dropHeroVideoAtDesktop(section, hero) {
    if (!window.matchMedia('(min-width: 1024px)').matches) return false;

    stripHeroMedia(hero.querySelector('[data-plp-hero-media="mobile"]'));

    hero.classList.add('plp__hero--no-anim', 'is-revealed');
    hero.style.setProperty('--plp-reveal-scale', '1');
    section.classList.remove('plp--hero-pending', 'plp--hero-ready');
    return true;
  }

  /**
   * The desktop hero film, mirroring the desktop homepage hero.
   *
   * It is rendered with preload="none" and no autoplay attribute, so it costs
   * nothing until play() is called here. Below 1024px it is never shown, so it
   * is stripped instead. On desktop it starts when the hero is actually on
   * screen and stops when it is not, and reduced motion leaves it paused.
   *
   * Nothing here reveals the element: it is visible from first paint showing
   * the film's own poster frame, so a pause, a refused autoplay or a failed
   * fetch all read as the film holding still rather than as another image
   * appearing underneath it.
   */
  function initDesktopHeroVideo(section) {
    var hero = section.querySelector('.plp__hero');
    if (!hero) return;

    if (hero._plpDesktopVideoCleanup) hero._plpDesktopVideoCleanup();

    var media = hero.querySelector('[data-plp-hero-media="desktop"]');
    if (!media) return;

    if (!window.matchMedia('(min-width: 1024px)').matches) {
      stripHeroMedia(media);
      return;
    }

    var video = media.querySelector('video');
    if (!video) return;

    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var visible = false;
    var observer = null;

    function stop() {
      try {
        video.pause();
      } catch (error) {
        /* Nothing to undo: the element simply had not started. */
      }
    }

    function sync() {
      if (!visible || motionQuery.matches) {
        stop();
        return;
      }
      if (!video.paused) return;
      // The poster paints first; the film waits for the page so it never takes
      // bandwidth from the product images. Retried on load below.
      if (document.readyState !== 'complete') return;
      // preload="none" means the element holds no data until it is asked for,
      // so the first start needs an explicit load().
      if (video.preload === 'none') {
        video.preload = 'metadata';
        video.load();
      }
      var started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(function () {
          // Autoplay refused, or the file failed. The still stays.
          log('desktop hero film did not start');
        });
      }
    }

    if (typeof IntersectionObserver === 'undefined') {
      visible = true;
      sync();
    } else {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
          });
          sync();
        },
        { rootMargin: '200px 0px' }
      );
      observer.observe(hero);
    }

    window.addEventListener('load', sync, { once: true });

    var onMotionChange = sync;
    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', onMotionChange);
    } else if (motionQuery.addListener) {
      motionQuery.addListener(onMotionChange);
    }

    hero._plpDesktopVideoCleanup = function () {
      if (observer) observer.disconnect();
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener('change', onMotionChange);
      } else if (motionQuery.removeListener) {
        motionQuery.removeListener(onMotionChange);
      }
      stop();
    };
  }

  function initHeroReveal(section, options) {
    var hero = section.querySelector('[data-plp-hero-video]');
    if (!hero) return;

    if (hero._plpHeroCleanup) hero._plpHeroCleanup();

    if (dropHeroVideoAtDesktop(section, hero)) return;

    options = options || {};
    var media = hero.querySelector('.plp__hero-media');
    var video = hero.querySelector('video');
    var viewport = window.visualViewport;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var disposed = false;
    var motionDone = false;
    var revealStarted = false;
    var mediaReady = false;
    var playAccepted = false;
    var revealTimer = null;
    var readinessTimer = null;
    var transitionTimer = null;

    function setRevealScale() {
      if (disposed || motionDone || revealStarted) return;
      var settledHeight = hero.offsetHeight;
      var viewportHeight = viewport ? viewport.height : window.innerHeight;
      if (!settledHeight || !viewportHeight) return;

      var scale = Math.max(1, viewportHeight / settledHeight);
      hero.style.setProperty('--plp-reveal-scale', scale.toFixed(4));
    }

    function removeResizeListeners() {
      window.removeEventListener('resize', setRevealScale);
      if (viewport) viewport.removeEventListener('resize', setRevealScale);
    }

    function removeEarlyListeners() {
      window.removeEventListener('wheel', onEarlyReveal);
      window.removeEventListener('touchstart', onEarlyReveal);
      window.removeEventListener('keydown', onEarlyReveal);
    }

    function removeMediaListeners() {
      if (!video) return;
      video.removeEventListener('loadeddata', onMediaReady);
      video.removeEventListener('canplay', onMediaReady);
      video.removeEventListener('error', onMediaFailure);
    }

    function clearRevealWork() {
      window.clearTimeout(revealTimer);
      window.clearTimeout(readinessTimer);
      removeEarlyListeners();
      removeMediaListeners();
      removeResizeListeners();
    }

    function finishReveal() {
      if (disposed || motionDone) return;
      motionDone = true;
      window.clearTimeout(transitionTimer);
      if (media) media.removeEventListener('transitionend', onTransitionEnd);
      section.classList.remove('plp--hero-pending', 'plp--hero-ready');
    }

    function onTransitionEnd(event) {
      if (disposed || motionDone) return;
      if (event.target !== media) return;
      if (['transform', 'height', 'top', 'left', 'width'].indexOf(event.propertyName) === -1) return;
      finishReveal();
    }

    function settleWithoutMotion(reason) {
      if (disposed || motionDone) return;
      log('settling hero without reveal motion:', reason);
      motionDone = true;
      revealStarted = true;
      clearRevealWork();
      window.clearTimeout(transitionTimer);
      if (media) media.removeEventListener('transitionend', onTransitionEnd);
      hero.classList.add('plp__hero--no-anim', 'is-revealed');
      hero.style.setProperty('--plp-reveal-scale', '1');
      section.classList.remove('plp--hero-pending', 'plp--hero-ready');
    }

    function reveal() {
      if (disposed || motionDone || revealStarted) return;
      revealStarted = true;
      window.clearTimeout(revealTimer);
      removeEarlyListeners();
      removeResizeListeners();
      if (media) media.addEventListener('transitionend', onTransitionEnd);
      hero.classList.add('is-revealed');
      transitionTimer = window.setTimeout(finishReveal, 1500);
    }

    function onEarlyReveal() {
      if (disposed || motionDone || revealStarted) return;
      if (!mediaReady || !playAccepted) {
        settleWithoutMotion('interaction before media readiness');
        return;
      }
      reveal();
    }

    function armReveal() {
      if (disposed || motionDone || revealStarted || !mediaReady || !playAccepted) return;
      window.clearTimeout(readinessTimer);
      removeMediaListeners();
      setRevealScale();
      if (media) media.offsetHeight;
      section.classList.add('plp--hero-ready');

      var initialDelay = Number(hero.getAttribute('data-reveal-delay')) || 3000;
      var authoredMobile = window.matchMedia('(max-width: 430px)').matches;
      var delay = options.fromSwap || authoredMobile ? 800 : initialDelay;
      revealTimer = window.setTimeout(reveal, delay);
    }

    function onMediaReady() {
      if (disposed || motionDone) return;
      mediaReady = true;
      armReveal();
    }

    function onMediaFailure() {
      if (disposed || motionDone) return;
      settleWithoutMotion('media error');
    }

    hero.classList.remove('plp__hero--no-anim', 'is-revealed');
    section.classList.remove('plp--hero-ready');
    section.classList.add('plp--hero-pending');
    setRevealScale();
    // Commit the measured intro transform before enabling its transition so
    // the CSS fallback cannot produce an unintended first-load zoom.
    if (media) media.offsetHeight;

    hero._plpHeroCleanup = function () {
      if (disposed) return;
      disposed = true;
      window.clearTimeout(revealTimer);
      window.clearTimeout(readinessTimer);
      window.clearTimeout(transitionTimer);
      removeEarlyListeners();
      removeMediaListeners();
      removeResizeListeners();
      if (media) media.removeEventListener('transitionend', onTransitionEnd);
      if (video) video.pause();
      section.classList.remove('plp--hero-pending', 'plp--hero-ready');
    };

    if (reduce) {
      if (video) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      hero.classList.add('plp__hero--no-anim', 'is-revealed');
      hero.style.setProperty('--plp-reveal-scale', '1');
      section.classList.remove('plp--hero-pending', 'plp--hero-ready');
      motionDone = true;
      return;
    }

    window.addEventListener('wheel', onEarlyReveal, { passive: true });
    window.addEventListener('touchstart', onEarlyReveal, { passive: true });
    window.addEventListener('keydown', onEarlyReveal);
    window.addEventListener('resize', setRevealScale, { passive: true });
    if (viewport) viewport.addEventListener('resize', setRevealScale, { passive: true });

    if (!media || !video) {
      settleWithoutMotion('video element unavailable');
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.addEventListener('loadeddata', onMediaReady);
    video.addEventListener('canplay', onMediaReady);
    video.addEventListener('error', onMediaFailure);

    if (options.fromSwap) {
      try {
        video.load();
      } catch (error) {
        settleWithoutMotion('video load failed');
        return;
      }
    }

    if (video.readyState >= 2) mediaReady = true;
    readinessTimer = window.setTimeout(function () {
      settleWithoutMotion('media readiness timeout');
    }, options.fromSwap ? 2500 : 6000);

    var playPromise;
    try {
      playPromise = video.play();
    } catch (error) {
      settleWithoutMotion('video play failed');
      return;
    }

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () {
        if (disposed || motionDone) return;
        playAccepted = true;
        armReveal();
      }).catch(function () {
        if (disposed || motionDone) return;
        settleWithoutMotion('video play rejected');
      });
    } else {
      playAccepted = true;
      armReveal();
    }
  }

  /* Native <details> removes closed content before a closing transition can
     finish. Keep `open` for the compositor reveal, then remove it. Track the
     desired state separately so rapid reversals cancel the pending close
     instead of racing timers. No content-height reads or inline sizing styles
     are used. */
  function initAnimatedDetails(details, contentSelector) {
    if (!details || details._plpAccordionCleanup) return;

    var summary = details.querySelector('summary');
    var content = details.querySelector(contentSelector);
    if (!summary || !content) return;

    var desiredOpen = details.open;
    var animationFrame = 0;
    var closeTimer = 0;
    var expectedToggleOpen = null;

    details.classList.add('is-accordion-managed');
    details.classList.toggle('is-expanded', desiredOpen);

    function syncAccessibleState() {
      summary.setAttribute('aria-expanded', String(desiredOpen));
      // Native closed <details> already removes descendants from focus/search.
      // Inert is needed only while `open` is intentionally retained for the
      // closing animation; removing it after close preserves find-in-page.
      content.toggleAttribute('inert', !desiredOpen && details.open);
    }

    function setNativeOpen(open) {
      if (details.open === open) return;
      expectedToggleOpen = open;
      details.open = open;
    }

    syncAccessibleState();

    function clearPending() {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (closeTimer) window.clearTimeout(closeTimer);
      animationFrame = 0;
      closeTimer = 0;
    }

    function finishClose() {
      if (desiredOpen) return;
      setNativeOpen(false);
      syncAccessibleState();
      closeTimer = 0;
    }

    function applyState() {
      clearPending();
      syncAccessibleState();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        details.classList.toggle('is-expanded', desiredOpen);
        setNativeOpen(desiredOpen);
        syncAccessibleState();
        return;
      }

      if (desiredOpen) {
        setNativeOpen(true);
        // Two cancellable frames commit the fully clipped starting mask before
        // revealing it, without a synchronous content-height measurement.
        animationFrame = window.requestAnimationFrame(function () {
          animationFrame = window.requestAnimationFrame(function () {
            animationFrame = 0;
            if (desiredOpen) details.classList.add('is-expanded');
          });
        });
        return;
      }

      details.classList.remove('is-expanded');
      if (!details.open) return;
      closeTimer = window.setTimeout(finishClose, 360);
    }

    function onSummaryClick(event) {
      event.preventDefault();
      desiredOpen = !desiredOpen;
      applyState();
    }

    function onNativeToggle() {
      if (expectedToggleOpen !== null && details.open === expectedToggleOpen) {
        expectedToggleOpen = null;
        return;
      }
      expectedToggleOpen = null;
      if (details.open === desiredOpen) return;
      desiredOpen = details.open;
      applyState();
    }

    function onContentTransitionEnd(event) {
      if (event.target !== content || event.propertyName !== 'clip-path') return;
      if (!desiredOpen) finishClose();
    }

    summary.addEventListener('click', onSummaryClick);
    details.addEventListener('toggle', onNativeToggle);
    content.addEventListener('transitionend', onContentTransitionEnd);

    details._plpAccordionCleanup = function () {
      clearPending();
      summary.removeEventListener('click', onSummaryClick);
      details.removeEventListener('toggle', onNativeToggle);
      content.removeEventListener('transitionend', onContentTransitionEnd);
      details.classList.remove('is-accordion-managed', 'is-expanded');
      summary.removeAttribute('aria-expanded');
      content.removeAttribute('inert');
      details.open = desiredOpen;
    };
  }

  function initAboutAccordions(section) {
    section.querySelectorAll('.plp__about-item').forEach(function (details) {
      initAnimatedDetails(details, '.plp__about-content');
    });
  }

  function teardownAboutAccordions(section) {
    if (!section) return;
    section.querySelectorAll('.plp__about-item').forEach(function (details) {
      if (!details._plpAccordionCleanup) return;
      details._plpAccordionCleanup();
      details._plpAccordionCleanup = null;
    });
  }

  function initSection(section, options) {
    if (!section) return;
    log('initSection', section.id);
    initHeroReveal(section, options);
    initDesktopHeroVideo(section);
    initAboutAccordions(section);
    initFilterDrawer(section);
    initProductCards(section);
    initDust(section);
    initSunGate(section);
  }

  function initSunGate(section) {
    var sun = section.querySelector('.plp__sun');
    var body = section.querySelector('.plp__body');
    if (!sun || !body || section._plpSunCleanup) return;
    if (!('IntersectionObserver' in window)) return;
    var visible = false;
    var mobile = window.matchMedia('(max-width: 1023px)');
    function update() {
      sun.classList.toggle('is-dim', !visible || document.hidden || !mobile.matches);
    }
    update();

    var observer = new IntersectionObserver(function (entries) {
      visible = entries[entries.length - 1].isIntersecting;
      update();
    }, { threshold: 0 });

    observer.observe(body);
    document.addEventListener('visibilitychange', update);
    mobile.addEventListener('change', update);
    section._plpSunCleanup = function () {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
      mobile.removeEventListener('change', update);
      section._plpSunCleanup = null;
    };
  }

  /**
   * The hero atmosphere (sun rays + dust) is a stack of large blurred conic
   * gradients sitting on permanently promoted compositor layers. It lives at
   * the very top of the page, but its cost was being paid on every frame no
   * matter where you were, which is what made scrolling past the about
   * accordions and the newsletter stutter.
   *
   * Measured on a 6x-throttled CPU while wheel-scrolling that region:
   * median frame 87ms and 81/130 frames over 50ms with the layers live,
   * versus median 17ms and 0 over 50ms with them idle. Pausing the
   * animations changed nothing (median stayed 83ms), so the cost is the
   * layers themselves rather than the ticking - which is why this gates
   * rendering instead of just pausing.
   *
   * The layers are purely decorative and aria-hidden, and they come back the
   * moment the hero is near the viewport again, so nothing is removed.
   */
  function initAtmosphereGate(section) {
    var hero = section.querySelector('.plp__hero');
    if (!hero || typeof IntersectionObserver === 'undefined') return;

    if (section._plpAtmosObserver) section._plpAtmosObserver.disconnect();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          section.classList.toggle('plp--atmos-idle', !entry.isIntersecting);
        });
      },
      { rootMargin: '300px 0px' }
    );

    observer.observe(hero);
    section._plpAtmosObserver = observer;
  }

  function initProductCards(section) {
    if (section._plpCardsBound) return;
    section._plpCardsBound = true;

    section.addEventListener('click', function (event) {
      var button = event.target.closest('[data-plp-card-atc]');
      if (!button || button.disabled) return;
      event.preventDefault();
      event.stopPropagation();

      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) {
        window.location.assign(button.getAttribute('data-product-url'));
        return;
      }

      var originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      var addRequest = window.PFProductActions.addVariant(Number(variantId), 1);
      var card = button.closest('.plp-card');
      var note = window.PFMotion && window.PFMotion.leafNote
        ? window.PFMotion.leafNote(card && card.getAttribute('data-plp-title')) : null;
      addRequest.then(function () {
        if (note) note.added();
        button.textContent = 'Added';
        window.setTimeout(function () {
          button.disabled = false;
          button.textContent = originalText;
        }, 1400);
      }).catch(function (error) {
        if (note) note.failed();
        button.disabled = false;
        button.textContent = originalText;
        window.alert(error.message);
      });
    });

    // The hover reveal (editorial + quick panel) keys off :hover OR :focus-within
    // in the stylesheet. A mouse click on a size pill / ATC button would leave
    // that button focused, so :focus-within stays true after the pointer leaves
    // and the editorial freezes on screen (until focus moves elsewhere) — the
    // shopper sees two hovers at once. Prevent focus from landing on a pointer
    // press instead: mousedown won't fire for keyboard activation, so Tab still
    // focuses the control and the keyboard reveal path is untouched, while a
    // mouse click no longer pins the card open.
    section.addEventListener('mousedown', function (event) {
      if (event.button !== 0) return;
      var control = event.target.closest(
        '[data-plp-size-pill], [data-plp-quick-add], [data-plp-quick-buy], [data-plp-card-atc]'
      );
      if (control) event.preventDefault();
    });

    // Re-apply the server-selected variant through the same atomic transition
    // used by clicks and filters. This is a safety net for cached section HTML
    // and cards whose preferred full variant became unavailable.
    section.querySelectorAll('.plp-card').forEach(function (card) {
      var selected = card.querySelector('[data-plp-size-pill].is-selected')
        || card.querySelector('[data-plp-size-pill][aria-pressed="true"]');
      if (selected) applyCardVariant(card, selected);
    });

    // Touch editorial reveal. A touchscreen has no hover, so the editorial
    // hovercard is driven by a deliberate tap instead (see the @media (hover:
    // none) block in plp-category.liquid, which neutralises the native :hover
    // and reveals via .is-revealed). First tap on a card reveals its editorial
    // (and does NOT navigate); the next tap on that same card follows the
    // overlay link to the PDP; the CTA still adds to cart (handled above). Tapping
    // another card swaps the reveal; tapping outside closes it. Desktop, which
    // matches (hover: hover), skips this entirely and uses native :hover.
    if (window.matchMedia('(hover: none)').matches && !section._plpTouchRevealBound) {
      section._plpTouchRevealBound = true;

      section.addEventListener('click', function (event) {
        var link = event.target.closest('.plp-card__link');
        if (!link) return; // CTA / quick-shop controls have their own handlers
        var card = link.closest('.plp-card');
        if (!card) return;
        if (card.classList.contains('is-revealed')) return; // second tap navigates
        event.preventDefault();
        section.querySelectorAll('.plp-card.is-revealed').forEach(function (c) {
          if (c !== card) c.classList.remove('is-revealed');
        });
        card.classList.add('is-revealed');
      }, true);

      // Tap outside any card closes the reveal.
      document.addEventListener('click', function (event) {
        if (event.target.closest('.plp-card')) return;
        section.querySelectorAll('.plp-card.is-revealed').forEach(function (c) {
          c.classList.remove('is-revealed');
        });
      });
    }
  }

  /* ------------------------------------------------------------------
     Filter & sort drawer
     ------------------------------------------------------------------ */

  var FILTER_DRAWER_OPEN_CLASS = 'is-open';
  var filterDrawerActive = null;

  function trapFocus(drawer) {
    var focusable = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function onKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    drawer._plpFocusTrap = onKeyDown;
    drawer.addEventListener('keydown', onKeyDown);
  }

  function releaseFocus(drawer) {
    if (drawer._plpFocusTrap) {
      drawer.removeEventListener('keydown', drawer._plpFocusTrap);
      drawer._plpFocusTrap = null;
    }
  }

  function openFilterDrawer(drawer, trigger) {
    if (filterDrawerActive) return;
    filterDrawerActive = drawer;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add(FILTER_DRAWER_OPEN_CLASS);
    document.documentElement.classList.add('plp-filter-open');
    trapFocus(drawer);

    // Escape has to close the panel wherever focus happens to be. The drawer's
    // own keydown listener only fires once focus is already inside it, so with
    // focus still on the trigger the key did nothing.
    drawer._plpEscape = function (event) {
      if (event.key !== 'Escape') return;
      if (!drawer.classList.contains(FILTER_DRAWER_OPEN_CLASS)) return;
      event.preventDefault();
      closeFilterDrawer(drawer);
    };
    document.addEventListener('keydown', drawer._plpEscape);

    // The panel is visibility:hidden until the open class takes effect, and
    // focus() on a hidden element is a no-op — so focus lands only after a
    // frame, otherwise it silently stayed on the trigger.
    // The backdrop div carries data-plp-filter-close too and comes first in
    // the DOM, so the bare attribute selector returned a non-focusable
    // <div> and initial focus never moved into the dialog.
    var closeButton = drawer.querySelector('button[data-plp-filter-close]');
    if (closeButton) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (drawer.classList.contains(FILTER_DRAWER_OPEN_CLASS)) {
            closeButton.focus({ preventScroll: true });
          }
        });
      });
    }

    drawer._plpTrigger = trigger || null;
  }

  function closeFilterDrawer(drawer) {
    drawer.classList.remove(FILTER_DRAWER_OPEN_CLASS);
    document.documentElement.classList.remove('plp-filter-open');
    releaseFocus(drawer);

    if (drawer._plpEscape) {
      document.removeEventListener('keydown', drawer._plpEscape);
      drawer._plpEscape = null;
    }

    window.clearTimeout(drawer._plpCloseTimer);
    drawer._plpCloseTimer = window.setTimeout(function () {
      drawer.setAttribute('aria-hidden', 'true');
      if (drawer._plpTrigger && document.contains(drawer._plpTrigger)) {
        drawer._plpTrigger.focus();
      }
      drawer._plpTrigger = null;
      drawer._plpCloseTimer = null;
      if (filterDrawerActive === drawer) filterDrawerActive = null;
    }, 300);
  }

  function teardownFilterDrawer(section) {
    if (!section) return;
    var drawer = section.querySelector('[data-plp-filter-drawer]');
    if (!drawer) return;

    window.clearTimeout(drawer._plpCloseTimer);
    drawer._plpCloseTimer = null;
    drawer.querySelectorAll('[data-plp-filter-group]').forEach(function (details) {
      if (!details._plpAccordionCleanup) return;
      details._plpAccordionCleanup();
      details._plpAccordionCleanup = null;
    });
    releaseFocus(drawer);
    if (drawer._plpEscape) {
      document.removeEventListener('keydown', drawer._plpEscape);
      drawer._plpEscape = null;
    }
    drawer.classList.remove(FILTER_DRAWER_OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'true');

    if (filterDrawerActive === drawer) {
      document.documentElement.classList.remove('plp-filter-open');
      filterDrawerActive = null;
    }
  }

  function initFilterAccordions(drawer) {
    drawer.querySelectorAll('[data-plp-filter-group]').forEach(function (details) {
      initAnimatedDetails(details, '.plp-filter__content');
    });
  }

  /* ------------------------------------------------------------------
     Money formatting
     ------------------------------------------------------------------
     The price slider labels are re-rendered as the handles move, so they have
     to be formatted the same way Liquid formatted the initial pair. The
     drawer carries the shop's own money_format for that, rather than a
     hardcoded symbol, so a currency change follows automatically.
     ------------------------------------------------------------------ */

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function groupDigits(number, decimals, thousands, decimalMark) {
    var fixed = Math.abs(number).toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
    return parts.length > 1 ? whole + decimalMark + parts[1] : whole;
  }

  function formatMoney(amount, format) {
    var value = Number(amount) || 0;
    if (!format) return String(Math.round(value));

    var match = format.match(MONEY_PLACEHOLDER);
    if (!match) return format;

    // Liquid rendered the initial labels with money_without_trailing_zeros, so
    // a whole amount must not gain a ".00" the moment the handle is nudged.
    var isWhole = value % 1 === 0;
    var body;

    switch (match[1]) {
      case 'amount_no_decimals':
        body = groupDigits(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        body = groupDigits(value, isWhole ? 0 : 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        body = groupDigits(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        body = groupDigits(value, isWhole ? 0 : 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        body = groupDigits(value, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        body = groupDigits(value, isWhole ? 0 : 2, "'", '.');
        break;
      default:
        body = groupDigits(value, isWhole ? 0 : 2, ',', '.');
    }

    return format.replace(MONEY_PLACEHOLDER, body);
  }

  function initPriceSlider(drawer) {
    var priceRoot = drawer.querySelector('[data-plp-filter-price]');
    if (!priceRoot) return;

    var minInput = priceRoot.querySelector('[data-plp-price-input="min"]');
    var maxInput = priceRoot.querySelector('[data-plp-price-input="max"]');
    var range = priceRoot.querySelector('[data-plp-price-range]');
    var minLabel = priceRoot.querySelector('[data-plp-price-label="min"]');
    var maxLabel = priceRoot.querySelector('[data-plp-price-label="max"]');
    if (!minInput || !maxInput) return;

    var moneyFormat = drawer.getAttribute('data-plp-money-format') || '';
    var min = Number(minInput.min);
    var max = Number(maxInput.max);
    var span = max - min;

    function update() {
      var vMin = Number(minInput.value);
      var vMax = Number(maxInput.value);
      if (vMin > vMax) {
        var tmp = vMin;
        vMin = vMax;
        vMax = tmp;
      }
      // A collection whose products all cost the same renders no slider at
      // all, but guard anyway so the track never divides by zero.
      var left = span > 0 ? ((vMin - min) / span) * 100 : 0;
      var right = span > 0 ? ((vMax - min) / span) * 100 : 100;
      if (range) {
        range.style.left = left + '%';
        range.style.width = (right - left) + '%';
      }
      if (minLabel) minLabel.textContent = formatMoney(vMin, moneyFormat);
      if (maxLabel) maxLabel.textContent = formatMoney(vMax, moneyFormat);

      // Both inputs span the whole track, so without this the one later in
      // the DOM always wins the pointer and a window dragged up against the
      // ceiling could never be opened back out - the minimum handle was
      // sitting underneath the maximum one with nothing left to grab.
      // Whichever handle the pair has drifted towards is raised.
      var reach = (vMin - min) + (vMax - min);
      var minOnTop = span > 0 && reach > span;
      minInput.style.zIndex = minOnTop ? '4' : '3';
      maxInput.style.zIndex = minOnTop ? '3' : '4';
    }

    minInput.addEventListener('input', update);
    maxInput.addEventListener('input', update);
    drawer._plpPriceUpdate = update;
    update();
  }

  function getFilterState(drawer) {
    var availability = '';
    var availabilityInput = drawer.querySelector('input[name="plp-availability"]:checked');
    if (availabilityInput) availability = availabilityInput.value;

    var size = '';
    var sizeInput = drawer.querySelector('input[name="plp-size"]:checked');
    if (sizeInput) size = sizeInput.value;

    var sort = '';
    var sortIsNative = false;
    var sortInput = drawer.querySelector('input[name="plp-sort"]:checked');
    if (sortInput) {
      sort = sortInput.value;
      sortIsNative = sortInput.hasAttribute('data-plp-sort-native');
    }

    // Liquid pre-ticks whichever sort the collection already applies, so a
    // shopper who only touched the size filter still arrives here with a
    // native sort selected. Treating that as a sort choice pinned sort_by to
    // the default on every apply and forced a server render for nothing.
    var sortIsDefault = sortIsNative && sort === drawer.getAttribute('data-plp-default-sort');

    var minInput = drawer.querySelector('[data-plp-price-input="min"]');
    var maxInput = drawer.querySelector('[data-plp-price-input="max"]');
    var priceFloor = minInput ? Number(minInput.min) : 0;
    var priceLimit = maxInput ? Number(maxInput.max) : Infinity;
    var priceMin = minInput ? Number(minInput.value) : priceFloor;
    var priceMax = maxInput ? Number(maxInput.value) : priceLimit;
    if (priceMin > priceMax) {
      var tmp = priceMin;
      priceMin = priceMax;
      priceMax = tmp;
    }

    return {
      availability: availability,
      size: size,
      sort: sort,
      sortIsNative: sortIsNative,
      sortIsDefault: sortIsDefault,
      priceFloor: priceFloor,
      priceMin: priceMin,
      priceMax: priceMax,
      priceLimit: priceLimit
    };
  }

  /* ------------------------------------------------------------------
     Applying a selection
     ------------------------------------------------------------------
     Sorting, and any filter the merchant has configured in Search &
     Discovery, are real server-side operations: they cover every page of the
     collection, not just the cards already in the DOM, so those go through
     the URL and re-render the section. Anything Shopify cannot filter for us
     falls back to hiding cards client-side, and rides along in the URL under
     pf_* so the selection survives a reload, a share, and Back/Forward.
     ------------------------------------------------------------------ */

  var CLIENT_PARAMS = ['pf_avail', 'pf_size', 'pf_min', 'pf_max', 'pf_sort'];

  function needsServerRender(drawer, state) {
    if (state.sortIsNative && !state.sortIsDefault) return true;
    if (state.availability && drawer.getAttribute('data-plp-native-availability')) return true;
    // A size selection narrows the collection server-side via Shopify's
    // variant-option filter, so it always needs a server render — the grid
    // has to come back from Shopify narrowed, and the param persists across
    // pagination only if it lives in the URL. Not gated on data-plp-native-size
    // because the variant-option URL filter works whether or not the merchant
    // has exposed the facet in Search & Discovery.
    if (state.size) return true;
    if (hasPriceWindow(state) && drawer.getAttribute('data-plp-native-price')) return true;

    // Clearing a server-applied selection is server work too. Without this,
    // Remove All on a sorted or Search & Discovery-filtered URL only dropped
    // the params and left the grid Shopify had already narrowed on screen.
    var current = new URLSearchParams(window.location.search);
    var carriesServerParams = false;
    current.forEach(function (value, key) {
      if (key === 'sort_by' || key.indexOf('filter.') === 0) carriesServerParams = true;
    });
    return carriesServerParams;
  }

  function hasPriceWindow(state) {
    return state.priceMin > state.priceFloor || state.priceMax < state.priceLimit;
  }

  function buildFilterUrl(drawer, state, serverRender) {
    // Only a server render is allowed to change route: off a collection page
    // the drawer's base is the collection that actually holds these products,
    // which is the only URL Shopify can sort and filter for real.
    var base = serverRender
      ? (drawer.getAttribute('data-plp-filter-base') || window.location.pathname)
      : window.location.pathname;

    var url = new URL(base, window.location.origin);

    // Carry over anything that is not ours and not stale — utm tags and the
    // like — but drop pagination, since page 3 of the old result set means
    // nothing once the result set changes.
    if (url.pathname === window.location.pathname) {
      new URLSearchParams(window.location.search).forEach(function (value, key) {
        if (key === 'sort_by' || key === 'page' || key === 'section_id') return;
        if (key.indexOf('filter.') === 0) return;
        if (CLIENT_PARAMS.indexOf(key) !== -1) return;
        url.searchParams.append(key, value);
      });
    }

    if (state.sort && state.sortIsNative && !state.sortIsDefault) url.searchParams.set('sort_by', state.sort);

    var nativeAvailability = drawer.getAttribute('data-plp-native-availability');
    var nativeSize = drawer.getAttribute('data-plp-native-size');
    var nativePrice = drawer.getAttribute('data-plp-native-price');

    if (state.availability) {
      if (nativeAvailability) {
        url.searchParams.set(nativeAvailability, state.availability === 'in-stock' ? '1' : '0');
      } else {
        url.searchParams.set('pf_avail', state.availability);
      }
    }

    if (state.size) {
      // Shopify's variant-option filter narrows the grid server-side and
      // survives pagination as a normal filter.* param. Use the Search &
      // Discovery facet's own param_name when it exists, else the conventional
      // filter.v.option.size — the filter engine honours it either way.
      url.searchParams.set(nativeSize || 'filter.v.option.size', state.size);
    }

    if (hasPriceWindow(state)) {
      if (nativePrice) {
        url.searchParams.set('filter.v.price.gte', state.priceMin);
        url.searchParams.set('filter.v.price.lte', state.priceMax);
      } else {
        url.searchParams.set('pf_min', state.priceMin);
        url.searchParams.set('pf_max', state.priceMax);
      }
    }

    // A non-native sort is applied to the cards in place, so it belongs with
    // the other client params rather than in sort_by, which Shopify would
    // reject.
    if (state.sort && !state.sortIsNative) url.searchParams.set('pf_sort', state.sort);

    return url.toString();
  }

  function submitFilters(section) {
    var drawer = section.querySelector('[data-plp-filter-drawer]');
    if (!drawer) return;

    var state = getFilterState(drawer);
    var serverRender = needsServerRender(drawer, state);
    var url = buildFilterUrl(drawer, state, serverRender);

    if (!serverRender) {
      // The address bar is updated first: the cross-page merge reads the
      // current URL to rebuild page 1 of this same result set.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(
          { plpCategory: true, url: url },
          '',
          url
        );
      }
      applyClientFiltersAcrossPages(section, state);
      return;
    }

    var target = new URL(url);
    if (target.pathname === window.location.pathname && swapCategory(url, true, true, 'push')) return;
    window.location.assign(url);
  }

  function compareCards(a, b, sort) {
    var titleA = (a.getAttribute('data-plp-title') || '').toLowerCase();
    var titleB = (b.getAttribute('data-plp-title') || '').toLowerCase();
    var priceA = Number(a.getAttribute('data-plp-price')) || 0;
    var priceB = Number(b.getAttribute('data-plp-price')) || 0;
    var dateA = Number(a.getAttribute('data-plp-date')) || 0;
    var dateB = Number(b.getAttribute('data-plp-date')) || 0;
    var orderA = Number(a.getAttribute('data-plp-order')) || 0;
    var orderB = Number(b.getAttribute('data-plp-order')) || 0;

    switch (sort) {
      case 'title-ascending':
        return titleA.localeCompare(titleB);
      case 'title-descending':
        return titleB.localeCompare(titleA);
      case 'price-ascending':
        return priceA - priceB;
      case 'price-descending':
        return priceB - priceA;
      case 'created-descending':
        return dateB - dateA;
      case 'created-ascending':
        return dateA - dateB;
      default:
        // manual / featured — the order Shopify already rendered them in.
        return orderA - orderB;
    }
  }

  /**
   * Shows the rendition matching `size` on one card, or restores the resting
   * front image when the size is cleared or has no art of its own.
   *
   * The card ships one front image plus a hidden sibling per size that carries
   * its own bottle (see plp-product-card.liquid). Only sizes with art render a
   * sibling, so a product whose smaller size was never shot simply keeps the
   * resting image - which is why binding art for one size is enough and no
   * card is left with a blank media box.
   */
  function setCardSizeImage(card, size) {
    var alts = card.querySelectorAll('[data-plp-size-img]');
    var hoverAlts = card.querySelectorAll('[data-plp-size-hover]');
    if (!alts.length && !hoverAlts.length) return;

    // Liquid can hide the product-level fallback when it renders a selected
    // variant-specific image. Once JavaScript owns the card, native `hidden`
    // state below is the single visibility mechanism.
    card.querySelectorAll('.plp-card__initial-hidden').forEach(function (image) {
      image.classList.remove('plp-card__initial-hidden');
    });

    var wanted = String(size || '').trim().toLowerCase();
    var matched = false;

    alts.forEach(function (alt) {
      var key = (alt.getAttribute('data-plp-size-img') || '').trim().toLowerCase();
      var hit = wanted !== '' && key === wanted;
      alt.hidden = !hit;
      if (hit) matched = true;
    });

    // The resting image is the only front rendition without a size key.
    var front = card.querySelector('.plp-card__img--front:not([data-plp-size-img])');
    if (front) front.hidden = matched;
    // The travel rendition stands shorter on the same ground line, so its ink
    // centre differs; the desktop text panel follows it (see .plp-card__info).
    card.classList.toggle('plp-card--travel-art', matched);

    // Mirror the swap on the editorial hovercard. Each size that carries its
    // own custom.plp_hover_image renders a hidden [data-plp-size-hover] sibling
    // (both the desktop hover img and the mobile scene); the resting editorial
    // is the default rendition with no size key. When a size-specific editorial
    // matches, it takes over and the default is hidden; otherwise the default
    // editorial stays so a size with no shot art falls back to the full editorial.
    var hoverMatched = false;

    hoverAlts.forEach(function (alt) {
      var key = (alt.getAttribute('data-plp-size-hover') || '').trim().toLowerCase();
      var hit = wanted !== '' && key === wanted;
      alt.hidden = !hit;
      if (hit) hoverMatched = true;
    });

    // The resting editorial is the only hover/scene rendition without a size key.
    var hoverDefault = card.querySelector('.plp-card__hover-default:not([data-plp-size-hover])');
    var sceneDefault = card.querySelector('.plp-card__scene--default:not([data-plp-size-hover])');
    if (hoverDefault) hoverDefault.hidden = hoverMatched;
    if (sceneDefault) sceneDefault.hidden = hoverMatched;
  }

  /**
   * Commits one complete product-variant state to a card. Every control and
   * visible value is changed in the same synchronous operation so a desktop
   * pill, a server-applied filter, or cached section HTML cannot leave the
   * mobile CTA submitting a different variant from the image and price shown.
   */
  function applyCardVariant(card, pill) {
    if (!card || !pill || pill.disabled) return;
    var quick = card.querySelector('[data-plp-quick]');
    if (!quick) return;

    quick.querySelectorAll('[data-plp-size-pill]').forEach(function (other) {
      var selected = other === pill;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    var variantId = pill.getAttribute('data-plp-variant-id');
    var cardLink = card.querySelector('.plp-card__link');
    if (variantId) {
      quick.setAttribute('data-plp-quick-id', variantId);
      if (cardLink) {
        var cardUrl = new URL(cardLink.href, window.location.origin);
        cardUrl.searchParams.set('variant', variantId);
        cardLink.href = cardUrl.toString();
      }

      var mobileAdd = card.querySelector('[data-plp-card-atc]');
      if (mobileAdd) {
        mobileAdd.setAttribute('data-variant-id', variantId);
        if (cardLink) mobileAdd.setAttribute('data-product-url', cardLink.href);
      }
    }

    var price = pill.getAttribute('data-plp-variant-price');
    var priceEl = card.querySelector('[data-plp-card-price]');
    var hoverPrice = card.querySelector('.plp-card__hv-price');
    if (priceEl && price) priceEl.textContent = price;
    if (hoverPrice && price) hoverPrice.textContent = price;
    if (cardLink && price) {
      cardLink.setAttribute('aria-label', (card.getAttribute('data-plp-title') || '') + ' — ' + price);
    }

    setCardSizeImage(card, pill.getAttribute('data-plp-variant-size'));
  }

  /**
   * Selects the size pill matching `size` on one card — the pill, the ATC
   * variant, the displayed price, and the art all retarget together, mirroring
   * the pill-click handler but driven by the active size filter rather than a
   * pointer. Used on load to make every card reflect the server-applied size
   * filter (filter.v.option.size) without a per-card click. A card with no
   * variant of that size (none expected once Shopify narrows the grid) is left
   * untouched.
   */
  function setCardSizePill(card, size) {
    var quick = card.querySelector('[data-plp-quick]');
    if (!quick) return;
    var wanted = String(size || '').trim().toLowerCase();
    if (wanted === '') return;
    var pill = null;
    quick.querySelectorAll('[data-plp-size-pill]').forEach(function (p) {
      if (p.disabled) return;
      var key = (p.getAttribute('data-plp-variant-size') || '').trim().toLowerCase();
      if (key === wanted) pill = p;
    });
    if (!pill) return;

    applyCardVariant(card, pill);
  }

  /**
   * Motion cut 8b, "filter settle". The old set exhales out, the filtered set
   * rises in — 500ms, no layout jump and no spinner. The filtering itself runs
   * in the gap while nothing is on screen, so a card never visibly jumps from
   * one slot to another.
   *
   * The first application on a page load is not a change of mind, it is the
   * URL being honoured, so that one lands instantly; only a filter the shopper
   * actually moved gets the crossfade.
   */
  function applyClientFilters(section, state) {
    var grid = section.querySelector('.plp__grid');
    var first = !section._plpFiltersApplied;
    section._plpFiltersApplied = true;

    if (first || !grid || !window.PFMotion) {
      applyClientFiltersNow(section, state);
      return;
    }

    window.PFMotion.settle(grid, function () {
      applyClientFiltersNow(section, state);
    });
  }

  function applyClientFiltersNow(section, state) {
    var grid = section.querySelector('.plp__grid');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.plp-card'));

    cards.forEach(function (card) {
      var available = card.getAttribute('data-plp-available') === 'true';
      var priceLow = Number(card.getAttribute('data-plp-price')) || 0;
      var priceHighAttr = card.getAttribute('data-plp-price-max');
      var priceHigh = Number(priceHighAttr);
      if (!priceHighAttr || !isFinite(priceHigh) || priceHigh < priceLow) priceHigh = priceLow;
      var size = card.getAttribute('data-plp-size') || '';

      var show = true;
      if (state.availability === 'in-stock' && !available) show = false;
      if (state.availability === 'out-of-stock' && available) show = false;
      if (state.size && size.toLowerCase().split('|').indexOf(state.size.toLowerCase()) === -1) show = false;
      // A product is in the window when any of its variants is, which is the
      // same rule Shopify's own storefront price filter applies - so the two
      // paths cannot disagree about which cards a given drag should leave.
      if (priceHigh < state.priceMin) show = false;
      if (priceLow > state.priceMax) show = false;

      card.hidden = !show;
      card.style.display = show ? '' : 'none';

      // Narrowing to a size is a statement about which bottle the shopper
      // means, so the art follows the filter. Clearing it restores the resting
      // image, including on a card whose pill was clicked earlier. When no size
      // filter is set, fall back to the card's pre-selected pill so a cleared
      // filter doesn't snap every card back to full and fight the pill.
      var pillSize = '';
      var selectedPill = card.querySelector('[data-plp-size-pill].is-selected')
        || card.querySelector('[data-plp-size-pill][aria-pressed="true"]');
      if (selectedPill) pillSize = selectedPill.getAttribute('data-plp-variant-size');
      setCardSizeImage(card, state.size || pillSize);
    });

    var visible = cards.filter(function (c) { return !c.hidden; });

    // A native sort arrived already applied from the server; re-ordering here
    // would only be able to guess at it (there is no per-card sales figure to
    // sort Best Selling by) and would fight the real order.
    if (state.sort && !state.sortIsNative) {
      visible.slice().sort(function (a, b) {
        return compareCards(a, b, state.sort);
      }).forEach(function (card) {
        grid.appendChild(card);
      });
    }

    var empty = section.querySelector('[data-plp-filter-empty]');
    if (empty) empty.hidden = visible.length !== 0;
  }

  /* ------------------------------------------------------------------
     Client-side facets across a paginated collection
     ------------------------------------------------------------------
     Availability, Size and the price window only fall back to hiding cards
     when Search & Discovery has not been set up to filter them for real, and
     hiding can only judge the cards that are in the DOM. On a paginated
     collection that is one page of them, so picking "50 ML" narrowed page 1
     and quietly ignored the rest of the shelf - the grid claimed to be
     showing every match while two thirds of the collection had never been
     looked at.

     So before any client-side pass runs, every page of the current result set
     is fetched and the grid is rebuilt from the whole of it. The selection is
     then applied to the entire collection, and the pagination - which now has
     nothing left to page through - is dropped.

     The walk always starts at page 1 rather than following "next" from
     wherever the shopper happens to be, because filtering from page 2 would
     otherwise have merged pages 2 and 3 and quietly left page 1 out.
     ------------------------------------------------------------------ */

  // A guard rather than a real limit: a collection long enough to need more
  // than this is one the merchant should be filtering through Search &
  // Discovery, which is server-side and needs none of this.
  var MAX_MERGED_PAGES = 12;

  function extraPageHtml(url) {
    return fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Page fetch failed: ' + response.status);
        return response.text();
      });
  }

  function nextPageHref(root) {
    var link = root.querySelector('.plp__pagination a[rel="next"]');
    if (!link) return null;
    var href = link.getAttribute('href');
    if (!href) return null;
    var url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url;
  }

  function gridTail(grid) {
    // Cards come first, then the empty-state paragraphs and the pagination -
    // so the first non-card is where the rebuilt run of cards has to end.
    var children = grid.children;
    for (var i = 0; i < children.length; i++) {
      if (!children[i].classList.contains('plp-card')) return children[i];
    }
    return null;
  }

  /**
   * Resolves once every page of the current result set is in the grid.
   * Cached per section, and a no-op on an unpaginated collection.
   */
  function loadEveryPage(section) {
    if (section._plpAllPages) return section._plpAllPages;

    var grid = section.querySelector('.plp__grid');
    if (!grid || !section.querySelector('.plp__pagination a[rel="next"], .plp__pagination a[rel="prev"]')) {
      section._plpAllPages = Promise.resolve();
      return section._plpAllPages;
    }

    var sectionKey = getSectionKey();
    // Page 1 of whatever is being shown: the sort and any storefront filter
    // have to ride along or the merged grid would be a different result set
    // from the one the shopper is looking at.
    var firstPage = new URL(window.location.href);
    firstPage.searchParams.delete('page');

    var collected = [];
    var complete = true;

    function pull(pageUrl, depth) {
      if (!pageUrl) return Promise.resolve();
      if (depth > MAX_MERGED_PAGES) {
        complete = false;
        warn('stopped merging at the ' + MAX_MERGED_PAGES + '-page guard');
        return Promise.resolve();
      }

      var requestUrl = sectionKey ? sectionRequestUrl(pageUrl.toString(), sectionKey) : pageUrl.toString();
      return extraPageHtml(requestUrl)
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          parsed.querySelectorAll('.plp__grid .plp-card').forEach(function (card) {
            collected.push(card);
          });
          return pull(nextPageHref(parsed), depth + 1);
        })
        .catch(function (err) {
          complete = false;
          warn('could not fetch', requestUrl, err);
        });
    }

    section._plpAllPages = pull(firstPage, 1).then(function () {
      // A half-finished walk is worse than none: it would replace the page the
      // shopper is on with a shorter set and look like a filter had already
      // been applied. The grid is left alone and the pass stays page-local.
      if (!complete || !collected.length) {
        // Not cached as the final answer: a page that failed once because the
        // network dropped should be tried again on the next Apply rather than
        // leaving the drawer permanently page-local for this visit.
        section._plpAllPages = null;
        warn('client-side filtering is limited to this page');
        return;
      }

      var anchorNode = gridTail(grid);
      grid.querySelectorAll('.plp-card').forEach(function (card) { card.remove(); });
      collected.forEach(function (card, index) {
        var imported = document.importNode(card, true);
        // Featured order is a position within a page, so every page restarts
        // at 1. Renumbered across the merged grid it is once again the order
        // the server rendered, which is what the client Featured sort means.
        imported.setAttribute('data-plp-order', String(index + 1));
        if (anchorNode) {
          grid.insertBefore(imported, anchorNode);
        } else {
          grid.appendChild(imported);
        }
      });

      var nav = section.querySelector('.plp__pagination');
      if (nav) nav.hidden = true;
      log('merged', collected.length, 'cards across every page for client-side filtering');
    });

    return section._plpAllPages;
  }

  function applyClientFiltersAcrossPages(section, state) {
    loadEveryPage(section).then(function () {
      applyClientFilters(section, state);
    });
  }

  /**
   * Puts the drawer back in step with the URL. Runs on every init, so a
   * shared link, a reload, a Back/Forward traversal and a category swap all
   * land on the same state rather than a cleared menu over a filtered grid.
   */
  function hydrateFilterDrawer(section, drawer) {
    var params = new URLSearchParams(window.location.search);

    function check(name, value) {
      if (!value) return;
      var input = drawer.querySelector(
        'input[name="' + name + '"][value="' + value.replace(/"/g, '\\"') + '"]'
      );
      if (input) input.checked = true;
    }

    var nativeAvailability = drawer.getAttribute('data-plp-native-availability');
    var nativeSize = drawer.getAttribute('data-plp-native-size');

    var availability = params.get('pf_avail') || '';
    if (!availability && nativeAvailability && params.has(nativeAvailability)) {
      availability = params.get(nativeAvailability) === '1' ? 'in-stock' : 'out-of-stock';
    }
    check('plp-availability', availability);

    var size = params.get('pf_size') || '';
    if (!size) size = params.get(nativeSize || 'filter.v.option.size') || '';
    check('plp-size', size);

    // A size filter is server-applied via filter.v.option.size, so the grid
    // came back already narrowed by Shopify. What it does NOT come back with is
    // the pills retargeted to that size — the card still defaults to the
    // full-size variant. Retarget every card's pill (selection, price, ATC,
    // and art) to the filtered size here so "all pills go to 50 ml" on load.
    if (size) {
      section.querySelectorAll('.plp-card').forEach(function (card) {
        setCardSizePill(card, size);
      });
    }

    // Liquid pre-checks the active native sort; only a client sort has to be
    // restored from the URL here.
    check('plp-sort', params.get('pf_sort') || '');

    var minInput = drawer.querySelector('[data-plp-price-input="min"]');
    var maxInput = drawer.querySelector('[data-plp-price-input="max"]');
    var urlMin = params.get('pf_min') || params.get('filter.v.price.gte');
    var urlMax = params.get('pf_max') || params.get('filter.v.price.lte');
    if (minInput && urlMin != null && urlMin !== '') minInput.value = urlMin;
    if (maxInput && urlMax != null && urlMax !== '') maxInput.value = urlMax;
    if (drawer._plpPriceUpdate) drawer._plpPriceUpdate();

    // Only the pf_* selections still need applying — anything native came back
    // from the server already filtered. Size is always server-applied now
    // (filter.v.option.size), so it never counts as client work; the pill
    // retarget above already handled it.
    var state = getFilterState(drawer);
    var hasClientWork =
      (!nativeAvailability && state.availability) ||
      (!drawer.getAttribute('data-plp-native-price') && hasPriceWindow(state)) ||
      (state.sort && !state.sortIsNative);

    if (hasClientWork) applyClientFiltersAcrossPages(section, state);
  }

  function resetFilters(drawer) {
    var inputs = drawer.querySelectorAll('[data-plp-filter-input]');
    inputs.forEach(function (input) { input.checked = false; });

    var minInput = drawer.querySelector('[data-plp-price-input="min"]');
    var maxInput = drawer.querySelector('[data-plp-price-input="max"]');
    if (minInput) minInput.value = minInput.min;
    if (maxInput) maxInput.value = maxInput.max;

    var event = new Event('input', { bubbles: true });
    if (minInput) minInput.dispatchEvent(event);
    if (maxInput) maxInput.dispatchEvent(event);
  }

  function initFilterDrawer(section) {
    var drawer = section.querySelector('[data-plp-filter-drawer]');
    if (!drawer) return;

    var trigger = section.querySelector('[data-plp-filter]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        openFilterDrawer(drawer, trigger);
      });
    }

    var closeButtons = drawer.querySelectorAll('[data-plp-filter-close]');
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeFilterDrawer(drawer);
      });
    });

    drawer.addEventListener('click', function (e) {
      if (e.target === drawer || e.target.classList.contains('plp-filter__backdrop')) {
        closeFilterDrawer(drawer);
      }
    });

    drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains(FILTER_DRAWER_OPEN_CLASS)) {
        e.preventDefault();
        closeFilterDrawer(drawer);
      }
    });

    initFilterAccordions(drawer);
    initPriceSlider(drawer);

    var applyButton = drawer.querySelector('[data-plp-filter-apply]');
    if (applyButton) {
      applyButton.addEventListener('click', function () {
        submitFilters(section);
        closeFilterDrawer(drawer);
      });
    }

    var resetButton = drawer.querySelector('[data-plp-filter-reset]');
    if (resetButton) {
      resetButton.addEventListener('click', function () {
        resetFilters(drawer);
        submitFilters(section);
      });
    }

    hydrateFilterDrawer(section, drawer);
  }

  /* ------------------------------------------------------------------
     No-reload category swap
     ------------------------------------------------------------------
     Uses Shopify's Section Rendering API to fetch only the target PLP
     section instead of downloading and parsing the entire page. Caches
     fetched HTML, aborts stale requests, and updates browser history.
     ------------------------------------------------------------------ */

  var PAGE_CACHE_TTL_MS = 15000;
  var pageCache = Object.create(null);
  var inflightController = null;
  var swapGeneration = 0;

  function cancelPendingSwap() {
    swapGeneration += 1;
    if (!inflightController) return;
    inflightController.abort();
    inflightController = null;
  }

  function invalidatePageCache() {
    pageCache = Object.create(null);
  }

  function cachedPage(requestUrl) {
    var entry = pageCache[requestUrl];
    if (!entry) return null;
    if (Date.now() - entry.cachedAt >= PAGE_CACHE_TTL_MS) {
      delete pageCache[requestUrl];
      return null;
    }
    return entry.html;
  }

  function currentCategoryWrapper() {
    var section = document.querySelector('[data-section-type="plp-category"]');
    return section && section.closest('.shopify-section');
  }

  function getSectionKey() {
    var wrapper = currentCategoryWrapper();
    if (!wrapper) return null;
    var id = wrapper.id || '';
    var key = id.replace(/^shopify-section-/, '');
    log('section key from wrapper id', id, '->', key);
    return key || 'plp_category';
  }

  function parseSectionHtml(html, sectionKey) {
    var parser = new DOMParser();
    var parsed = parser.parseFromString(html, 'text/html');

    var newWrapper = parsed.getElementById('shopify-section-' + sectionKey);
    if (!newWrapper) {
      newWrapper = parsed.querySelector('[data-section-type="plp-category"]');
      if (newWrapper) {
        newWrapper = newWrapper.closest('.shopify-section');
      }
    }

    if (newWrapper) {
      newWrapper.querySelectorAll('noscript[data-plp-hero-noscript]').forEach(function (fallback) {
        fallback.remove();
      });
    }

    return newWrapper;
  }

  function forceScrollTop() {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (error) {
      window.scrollTo(0, 0);
    }
  }

  function applyNewSection(newWrapper, url, pushHistory, navigationKind) {
    var wrapper = currentCategoryWrapper();
    if (!wrapper) {
      throw new Error('Current plp-category wrapper disappeared');
    }

    var oldSection = wrapper.querySelector('[data-section-type="plp-category"]');
    if (oldSection && oldSection._plpSunCleanup) oldSection._plpSunCleanup();
    teardownHeroReveal(oldSection);
    teardownAboutAccordions(oldSection);
    teardownFilterDrawer(oldSection);
    wrapper.parentNode.replaceChild(newWrapper, wrapper);

    var newSection = document.querySelector('[data-section-type="plp-category"]');
    forceScrollTop();

    // The address bar is updated before the section is initialised, because
    // the filter drawer restores its own selection by reading the URL. Pushed
    // afterwards, it read the URL being navigated away from and came back with
    // the previous filter still ticked.
    if (pushHistory && window.history && window.history.pushState) {
      window.history.pushState({ plpCategory: true, url: url }, '', url);
    }

    initSection(newSection, { fromSwap: true, navigationKind: navigationKind });

    // Browsers can apply traversal scroll restoration after popstate returns.
    // Reassert the PLP contract on the next frame so Back/Forward swaps also
    // begin at the top rather than revealing above the restored viewport.
    if (navigationKind === 'pop') {
      window.requestAnimationFrame(forceScrollTop);
    }

    log('swap complete for', url);
  }

  function sectionRequestUrl(url, sectionKey) {
    var requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionKey);
    return requestUrl.toString();
  }

  function fetchCategoryPage(url, sectionKey) {
    var requestUrl = sectionRequestUrl(url, sectionKey);
    var cachedHtml = cachedPage(requestUrl);
    var designMode = window.Shopify && window.Shopify.designMode;

    if (inflightController) {
      log('aborting stale request');
      inflightController.abort();
      inflightController = null;
    }

    if (cachedHtml != null && !designMode) {
      log('serving cached page', url);
      return Promise.resolve(cachedHtml);
    }

    var controller = new AbortController();
    inflightController = controller;

    log('fetching PLP section', requestUrl);

    return fetch(requestUrl, {
      signal: controller.signal,
      cache: 'no-store',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (response) {
        log('fetch response', response.status);
        if (!response.ok) throw new Error('Network response was not ok: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        pageCache[requestUrl] = { html: html, cachedAt: Date.now() };
        if (inflightController === controller) inflightController = null;
        return html;
      })
      .catch(function (err) {
        if (inflightController === controller) inflightController = null;
        throw err;
      });
  }

  function swapCategory(url, fallbackToNav, pushHistory, navigationKind) {
    if (pushHistory == null) pushHistory = true;
    if (!navigationKind) navigationKind = pushHistory ? 'push' : 'pop';

    var sectionKey = getSectionKey();
    if (!sectionKey) {
      warn('Could not determine section key');
      return false;
    }

    var wrapper = currentCategoryWrapper();
    if (!wrapper) {
      warn('No plp-category wrapper found');
      return false;
    }

    var generation = ++swapGeneration;

    /* Motion cut 8b, "filter settle", on the category rail: the set on screen
       exhales the moment the pill is pressed, the new one rises in when it
       lands. The exhale is started here rather than around the DOM swap so it
       covers the fetch — the shopper gets an answer to the tap immediately,
       instead of a frozen grid followed by an abrupt replacement. */
    if (window.PFMotion) window.PFMotion.settleOut(wrapper);

    function releaseSettle() {
      if (!window.PFMotion) return;
      var live = currentCategoryWrapper() || wrapper;
      window.PFMotion.settleIn(live);
    }

    fetchCategoryPage(url, sectionKey)
      .then(function (html) {
        if (generation !== swapGeneration) {
          log('ignoring stale category response for', url);
          return;
        }
        var newWrapper = parseSectionHtml(html, sectionKey);
        if (!newWrapper) {
          throw new Error('Could not find section wrapper in fetched page');
        }
        applyNewSection(newWrapper, url, pushHistory, navigationKind);
        releaseSettle();
      })
      .catch(function (err) {
        // Whatever happens, the grid must not be left holding its breath.
        releaseSettle();
        if (generation !== swapGeneration) {
          log('ignoring stale category failure for', url);
          return;
        }
        if (err.name === 'AbortError') {
          log('request aborted for', url);
          return;
        }
        warn('Category swap failed, falling back to full navigation:', err);
        if (fallbackToNav) window.location.assign(url);
      });

    return true;
  }

  /* ------------------------------------------------------------------
     Pill interaction: click + prefetch
     ------------------------------------------------------------------ */

  function pillUrl(link) {
    var href = link.getAttribute('href');
    if (!href) return null;
    var url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    return url.toString();
  }

  function onPillClick(event) {
    var link = event.target.closest('[data-plp-pill]');
    if (!link) return;

    var url = pillUrl(link);
    if (!url) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.getAttribute('target') === '_blank') return;

    event.preventDefault();
    log('pill click', url);

    var swapped = swapCategory(url, true, true, 'push');
    if (!swapped) {
      log('swap not possible, navigating normally');
      window.location.assign(url);
    }
  }

  /* ------------------------------------------------------------------
     Browser Back/Forward
     ------------------------------------------------------------------ */

  function onPopState(event) {
    var state = event.state;
    if (!state || !state.plpCategory || !state.url) return;

    log('popstate back/forward to', state.url);
    var swapped = swapCategory(state.url, false, false, 'pop');
    if (!swapped) {
      window.location.assign(state.url);
    }
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function boot() {
    if (window.__plpCategoryBooted) return;
    window.__plpCategoryBooted = true;

    log('booted. Add ?plp-debug=1 to the URL to see detailed logs.');
    var sections = Array.from(document.querySelectorAll('[data-section-type="plp-category"]'));
    log('found sections:', sections.length);
    sections.forEach(function (section) {
      initSection(section);
    });

    document.addEventListener('click', onPillClick, false);

    if (window.history && window.history.pushState) {
      window.addEventListener('popstate', onPopState, false);
      if (!window.history.state || !window.history.state.plpCategory) {
        window.history.replaceState(
          { plpCategory: true, url: window.location.href },
          '',
          window.location.href
        );
      }
    }

    // Shopify replaces section markup live inside the theme editor. Re-run
    // the section-specific bindings so previews remain interactive after an
    // image, description, or other setting is changed.
    document.addEventListener('shopify:section:load', function (event) {
      invalidatePageCache();
      var section = event.target.querySelector('[data-section-type="plp-category"]');
      if (section) initSection(section);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var section = event.target.querySelector('[data-section-type="plp-category"]');
      if (section) {
        if (section._plpSunCleanup) section._plpSunCleanup();
        cancelPendingSwap();
        teardownHeroReveal(section);
        teardownAboutAccordions(section);
        teardownFilterDrawer(section);
      }
    });

    document.addEventListener('shopify:block:select', invalidatePageCache);
    document.addEventListener('shopify:block:deselect', invalidatePageCache);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) invalidatePageCache();
    });

    bindQuickShop();

    window.__plpSwapCategory = swapCategory;
    window.__plpInvalidateCache = invalidatePageCache;
  }

  /**
   * Size selector inside the desktop hover variant (Figma 2121:1533).
   * Delegated from the document so it survives a category swap replacing the
   * whole section, and bound once. Selecting a size retargets the card's
   * add-to-cart form and rewrites the visible price, because the variants are
   * not all the same price and leaving the resting minimum on screen next to a
   * selected larger size would quote a price the form would not charge.
   */
  function bindQuickShop() {
    if (window.__plpQuickShopBound) return;
    window.__plpQuickShopBound = true;

    document.addEventListener('click', function (event) {
      var target = event.target;
      // Must match the pill only. The card root carries data-plp-size as its
      // filterable size *value*, so a closest() on that name matched every
      // click anywhere on the card - including the full-card overlay link -
      // and killed navigation on every product card. Hence the -pill suffix.
      var pill = target && target.closest ? target.closest('[data-plp-size-pill]') : null;
      if (!pill || pill.disabled) return;

      // The whole card is an overlay link; the pill sits above it and must not
      // navigate.
      event.preventDefault();
      event.stopPropagation();

      var card = pill.closest('.plp-card');
      if (card) applyCardVariant(card, pill);
    });

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var button = target.closest('[data-plp-quick-add], [data-plp-quick-buy]');
      if (!button || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      var quick = button.closest('[data-plp-quick]');
      var variantId = quick && quick.getAttribute('data-plp-quick-id');
      if (!variantId) return;

      var checkout = button.hasAttribute('data-plp-quick-buy');
      var restoreLabel = button.textContent;
      button.disabled = true;
      button.textContent = button.getAttribute('data-plp-busy-label') || 'Adding…';

      /* Buy now buys this card's product only. It lends the cart to that one
         line for the checkout and hands the shopper's own bag back when they
         return, instead of appending to the cart and charging for all of it.
         See assets/pf-buy-now.js. */
      if (checkout && window.PFBuyNow) {
        window.PFBuyNow
          .start(
            [{ id: Number(variantId), quantity: 1 }],
            (window.PFRoutes && window.PFRoutes.checkout) || '/checkout'
          )
          .catch(function (error) {
            button.disabled = false;
            button.textContent = restoreLabel;
            window.alert((error && error.message) || 'Unable to start checkout');
          });
        return;
      }

      var addRequest = window.PFProductActions.addVariant(Number(variantId), 1);
      var card = button.closest('.plp-card');
      var note = !checkout && window.PFMotion && window.PFMotion.leafNote
        ? window.PFMotion.leafNote(card && card.getAttribute('data-plp-title')) : null;
      addRequest
        .then(function () {
          if (checkout) {
            // Route through the theme's own pre-checkout screen when one exists.
            var target = (window.PFRoutes && window.PFRoutes.checkout) || '/checkout';
            window.location.assign(target);
            return;
          }
          if (note) note.added();
          button.textContent = button.getAttribute('data-plp-added-label') || 'Added';
          window.setTimeout(function () {
            button.disabled = false;
            button.textContent = restoreLabel;
          }, 1400);
        })
        .catch(function (error) {
          if (note) note.failed();
          button.disabled = false;
          button.textContent = restoreLabel;
          window.alert(error.message);
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
